import React, { useEffect, useState, useContext } from "react";
import CompanyLayout from "../../../layout/CompanyLayout";
import { getCompanyDashboardData } from "../../../../api/dashboard.api";
import { FaBullhorn, FaHourglass, FaCheckCircle, FaPlay } from "react-icons/fa";
import { MoonLoader } from "react-spinners";
import { UserContext, useUser } from "../../../../context/UserContext";

const initialUserDashboardStats = [
  {
    title: "Total Campaigns",
    value: 0,
    color: "#6366f1",
    bgColor: "bg-indigo-500",
    lightBg: "bg-indigo-50",
    icon: <FaBullhorn className="text-2xl" />,
    subtitle: "This month",
  },
  {
    title: "Pending Approval",
    value: 0,
    color: "#f59e0b",
    bgColor: "bg-amber-500",
    lightBg: "bg-amber-50",
    icon: <FaHourglass className="text-2xl" />,

    subtitle: "Awaiting review",
  },
  {
    title: "Approved",
    value: 0,
    color: "#10b981",
    bgColor: "bg-emerald-500",
    lightBg: "bg-emerald-50",
    icon: <FaCheckCircle className="text-2xl" />,

    subtitle: "Ready to launch",
  },
  {
    title: "Active Now",
    value: 0,
    color: "#8b5cf6",
    bgColor: "bg-violet-500",
    lightBg: "bg-violet-50",
    icon: <FaPlay className="text-2xl" />,

    subtitle: "Currently running",
  },
];

function UserDashboard() {
  const [userDashboardStats, setUserDashboardStats] = useState(
    initialUserDashboardStats
  );
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const { user } = useContext(UserContext);
  const { currentView } = useUser();
  useEffect(() => {
    getCompanyDashboardData()
      .then((res) => {
        const data = res?.data?.data;
        console.log("Dashboard Data:", data);
        if (data) {
          const updatedStats = initialUserDashboardStats.map((stat, index) => {
            switch (index) {
              case 0:
                return { ...stat, value: data.totalCampaigns || stat.value };
              case 1:
                return { ...stat, value: data.pendingCampaigns || stat.value };
              case 2:
                return { ...stat, value: data.approvedCampaigns || stat.value };
              case 3:
                return { ...stat, value: data.ongoingCampaigns || stat.value };
              default:
                return stat;
            }
          });

          setUserDashboardStats(updatedStats);
        }
      })
      .catch((error) => {
        console.error("Error fetching user dashboard data:", error);
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => setIsVisible(true), 200);
      });
  }, []);

  const AnimatedNumber = ({ value, duration = 2000 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      if (isVisible && value > 0) {
        const increment = value / (duration / 50);
        let current = 0;
        const timer = setInterval(() => {
          current += increment;
          if (current >= value) {
            setDisplayValue(value);
            clearInterval(timer);
          } else {
            setDisplayValue(Math.floor(current));
          }
        }, 50);
        return () => clearInterval(timer);
      } else {
        setDisplayValue(value);
      }
    }, [value, isVisible, duration]);

    return displayValue;
  };

  return (
    <CompanyLayout
      profileDetails={user}
      title={`Company Dashboard - ${
        currentView === "owner" ? "Owner View" : "Company View"
      }`}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 md:ml-4">
          <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  Dashboard
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  Welcome back! Here's what's happening with your campaigns
                  today.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-8 py-8">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-96">
              <MoonLoader color="#6366f1" size={40} />
              <p className="mt-4 text-gray-600">Loading dashboard...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {userDashboardStats.map((stat, index) => (
                  <div
                    key={index}
                    className={`relative bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 group ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-4 opacity-0"
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    {/* Color accent */}
                    <div
                      className="absolute top-0 left-0 w-full h-1 rounded-t-xl"
                      style={{ backgroundColor: stat.color }}
                    ></div>

                    {/* Content */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.lightBg} text-white transition-transform duration-300 group-hover:scale-110`}
                            style={{ backgroundColor: stat.color }}
                          >
                            {stat.icon}
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-600">
                              {stat.title}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {stat.subtitle}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-baseline space-x-2">
                            <span className="text-3xl font-bold text-gray-900">
                              <AnimatedNumber value={stat.value} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </CompanyLayout>
  );
}

export default UserDashboard;
